import { useSignalR } from '../components/providers/SignalRProvider.vue'
import { createSubscriptionAdder } from './subscription-utils'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  StakeholderPostalAddressChangedSignalRPayload,
  StakeholderGeneralInfoChangedSignalRPayload,
  StakeholderVisitAddressChangedSignalRPayload,
  StakeholderRemovedSignalRPayload,
  StakeholderRestoredSignalRPayload,
  StakeholderAddedSignalRPayload,
  StakeholderInternalIdentificationNumbersChangedSignalRPayload,
  StakeholderOfficialBusinessNumbersChangedSignalRPayload,
  StakeholderBillingDetailsChangedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type StakeholderEventHandlers = {
  onStakeholderAdded?: (
    payload: SignalRReceiveEvent<StakeholderAddedSignalRPayload>
  ) => void
  onStakeholderGeneralInfoChanged?: (
    payload: SignalRReceiveEvent<StakeholderGeneralInfoChangedSignalRPayload>
  ) => void
  onStakeholderInternalIdentificationNumbersChanged?: (
    payload: SignalRReceiveEvent<StakeholderInternalIdentificationNumbersChangedSignalRPayload>
  ) => void
  onStakeholderOfficialBusinessNumbersChanged?: (
    payload: SignalRReceiveEvent<StakeholderOfficialBusinessNumbersChangedSignalRPayload>
  ) => void
  onStakeholderPostalAddressChanged?: (
    payload: SignalRReceiveEvent<StakeholderPostalAddressChangedSignalRPayload>
  ) => void
  onStakeholderVisitAddressChanged?: (
    payload: SignalRReceiveEvent<StakeholderVisitAddressChangedSignalRPayload>
  ) => void
  onStakeholderRemoved?: (
    payload: SignalRReceiveEvent<StakeholderRemovedSignalRPayload>
  ) => void
  onStakeholderRestored?: (
    payload: SignalRReceiveEvent<StakeholderRestoredSignalRPayload>
  ) => void
  onStakeholderBillingDetailsChanged?: (
    payload: SignalRReceiveEvent<StakeholderBillingDetailsChangedSignalRPayload>
  ) => void
}

export function useStakeholderSignalREvents(
  handlers?: StakeholderEventHandlers
) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToOtherPartyAdded(
    cb: (payload: SignalRReceiveEvent<StakeholderAddedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderAddedSignalRPayload>(
      signalREvent.Stakeholder.V1.Added,
      cb,
      options
    )
  }

  function subscribeToOtherPartyGeneralChanged(
    cb: (
      payload: SignalRReceiveEvent<StakeholderGeneralInfoChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderGeneralInfoChangedSignalRPayload>(
      signalREvent.Stakeholder.V1.GeneralInfoChanged,
      cb,
      options
    )
  }

  function subscribeToOtherPartyInternalIdentificationNumbersChanged(
    cb: (
      payload: SignalRReceiveEvent<StakeholderInternalIdentificationNumbersChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderInternalIdentificationNumbersChangedSignalRPayload>(
      signalREvent.Stakeholder.V1.InternalIdentificationNumbersChanged,
      cb,
      options
    )
  }

  function subscribeToOtherPartyOfficialBusinessNumbersChanged(
    cb: (
      payload: SignalRReceiveEvent<StakeholderOfficialBusinessNumbersChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderOfficialBusinessNumbersChangedSignalRPayload>(
      signalREvent.Stakeholder.V1.OfficialBusinessNumbersChanged,
      cb,
      options
    )
  }

  function subscribeToOtherPartyPostalAddressChanged(
    cb: (
      payload: SignalRReceiveEvent<StakeholderPostalAddressChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderPostalAddressChangedSignalRPayload>(
      signalREvent.Stakeholder.V1.PostalAddressChanged,
      cb,
      options
    )
  }

  function subscribeToOtherPartyVisitAddressChanged(
    cb: (
      payload: SignalRReceiveEvent<StakeholderVisitAddressChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderVisitAddressChangedSignalRPayload>(
      signalREvent.Stakeholder.V1.VisitAddressChanged,
      cb,
      options
    )
  }

  function subscribeToOtherPartyDeactivated(
    cb: (
      payload: SignalRReceiveEvent<StakeholderRemovedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderRemovedSignalRPayload>(
      signalREvent.Stakeholder.V1.Removed,
      cb,
      options
    )
  }

  function subscribeToOtherPartyReactivated(
    cb: (
      payload: SignalRReceiveEvent<StakeholderRestoredSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderRestoredSignalRPayload>(
      signalREvent.Stakeholder.V1.Restored,
      cb,
      options
    )
  }

  function subscribeToOtherPartyBillingDetailsChanged(
    cb: (
      payload: SignalRReceiveEvent<StakeholderBillingDetailsChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderBillingDetailsChangedSignalRPayload>(
      signalREvent.Stakeholder.V1.BillingDetailsChanged,
      cb,
      options
    )
  }

  function subscribeToStakeholderEvents() {
    console.log('subscribeToStakeholderEvents')
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []
      const addSubscription = createSubscriptionAdder(unsubscribeFunctions)

      addSubscription(handlers.onStakeholderAdded, subscribeToOtherPartyAdded)
      addSubscription(
        handlers.onStakeholderGeneralInfoChanged,
        subscribeToOtherPartyGeneralChanged
      )
      addSubscription(
        handlers.onStakeholderInternalIdentificationNumbersChanged,
        subscribeToOtherPartyInternalIdentificationNumbersChanged
      )
      addSubscription(
        handlers.onStakeholderOfficialBusinessNumbersChanged,
        subscribeToOtherPartyOfficialBusinessNumbersChanged
      )
      addSubscription(
        handlers.onStakeholderVisitAddressChanged,
        subscribeToOtherPartyVisitAddressChanged
      )
      addSubscription(
        handlers.onStakeholderPostalAddressChanged,
        subscribeToOtherPartyPostalAddressChanged
      )
      addSubscription(
        handlers.onStakeholderRemoved,
        subscribeToOtherPartyDeactivated
      )
      addSubscription(
        handlers.onStakeholderRestored,
        subscribeToOtherPartyReactivated
      )
      addSubscription(
        handlers.onStakeholderBillingDetailsChanged,
        subscribeToOtherPartyBillingDetailsChanged
      )

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromStakeholderEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToStakeholderEvents,
    unsubscribeFromStakeholderEvents
  }
}
